import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, XCircle, CheckCircle2, BookOpen, ShieldCheck, ArrowRight, Download, BookOpenCheck, Users } from 'lucide-react';
import { FAQItem } from './FAQItem';
import { LegalModal } from './LegalModal';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay
}} data-id="element-9">
    {children}
  </motion.div>;
export function LandingPage() {
  const [legalModal, setLegalModal] = useState<{
    open: boolean;
    type: 'privacy' | 'terms';
  }>({
    open: false,
    type: 'privacy'
  });
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream font-sans text-earth-dark selection:bg-terracotta/20" data-id="element-10">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-6 overflow-hidden" data-id="element-11">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-terracotta/5 via-cream to-cream -z-10" data-id="element-12"></div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-13">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="space-y-8" data-id="element-14">
            <div className="inline-flex items-center space-x-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full border border-terracotta/20 text-terracotta-dark text-sm font-medium tracking-wide mb-4" data-id="element-15">
              <Heart size={16} className="text-terracotta" data-id="element-16" />
              <span data-id="element-17">Para madres latinas reales</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-earth-dark" data-id="element-18">
              Mamá, no es que tu hijo no te respete...{' '}
              <span className="text-terracotta italic" data-id="element-19">
                es que su cerebro procesa el mundo a una velocidad distinta.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-earth-medium leading-relaxed" data-id="element-20">
              La guía definitiva para madres latinas que quieren dejar de
              gritar, sanar la culpa y lograr que sus hijos con ADHD finalmente
              escuchen (sin romper su espíritu).
            </p>
            <button onClick={scrollToOffer} className="w-full md:w-auto bg-terracotta hover:bg-terracotta-dark text-white font-semibold py-4 px-8 rounded-2xl shadow-lg shadow-terracotta/30 transition-all duration-300 hover:-translate-y-1 flex items-center justify-center space-x-3 group" data-id="element-21">
              <span data-id="element-22">QUIERO RECUPERAR LA PAZ EN MI HOGAR AHORA</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} data-id="element-23" />
            </button>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-24">
            <div className="absolute inset-0 bg-terracotta/10 rounded-[2rem] transform rotate-3 scale-105 -z-10" data-id="element-25"></div>
            <img src="https://cdn.magicpatterns.com/uploads/5eGXSMnvGheEsPkExMbkqH/Opcion_5_meta_vertical_9x16.png" alt="Guía Mi Hijo No Me Hace Caso" className="w-full max-w-md mx-auto rounded-2xl shadow-2xl object-cover" data-id="element-26" />
          </motion.div>
        </div>
      </section>

      {/* OPENING HOOK */}
      <section className="py-20 px-6 bg-white" data-id="element-27">
        <div className="max-w-3xl mx-auto" data-id="element-28">
          <FadeIn data-id="element-29">
            <div className="prose prose-lg prose-p:text-earth-medium prose-p:leading-relaxed mx-auto text-center" data-id="element-30">
              <p className="text-xl md:text-2xl font-serif italic text-earth-dark mb-8" data-id="element-31">
                "Estás en la cocina, con el nudo en la garganta, después de
                haber gritado por quinta vez que se ponga los zapatos. Él ni
                siquiera te miró. Sientes que has fallado, que el control se te
                escapó de las manos y que la escuela tiene razón cuando te llama
                para decirte que 'es un niño difícil'."
              </p>
              <p className="text-lg font-medium" data-id="element-32">
                Pero déjame decirte algo que nadie te ha dicho:{' '}
                <strong className="text-terracotta" data-id="element-33">
                  No eres una mala madre.
                </strong>{' '}
                Estás agotada porque estás intentando apagar un incendio con las
                manos vacías.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-6 bg-cream-dark" data-id="element-34">
        <div className="max-w-4xl mx-auto" data-id="element-35">
          <FadeIn data-id="element-36">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-soft" data-id="element-37">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-10 text-earth-dark" data-id="element-38">
                El ciclo que te está drenando la vida
              </h2>
              <div className="space-y-6 text-lg text-earth-medium" data-id="element-39">
                <p data-id="element-40">
                  Vives en un ciclo que te está drenando la vida. Pides algo, él
                  te ignora, tú repites, él te desafía, tú gritas, él llora y
                  luego llega esa culpa espantosa que no te deja dormir.
                </p>
                <p data-id="element-41">
                  Sientes la mirada de tu suegra o de la gente en el
                  supermercado juzgando tu crianza. Sientes que nada de lo que
                  haces funciona y que la relación con tu hijo se está
                  convirtiendo en una zona de guerra diaria.
                </p>
                <div className="bg-terracotta/10 p-6 rounded-2xl border-l-4 border-terracotta mt-8" data-id="element-42">
                  <p className="font-medium text-earth-dark m-0" data-id="element-43">
                    El ADHD no es una excusa, pero es la razón por la cual los
                    métodos de "mano dura" tradicionales solo empeoran las
                    cosas.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 px-6" data-id="element-44">
        <div className="max-w-4xl mx-auto" data-id="element-45">
          <FadeIn data-id="element-46">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-16 text-earth-dark" data-id="element-47">
              Por qué lo que has intentado no funciona
            </h2>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-48">
              {['Los gritos solo activan el modo de supervivencia en su cerebro, bloqueando cualquier aprendizaje real.', 'Los castigos excesivos generan resentimiento y bajan aún más su ya frágil autoestima.', "Los consejos de 'mano dura' de familiares no toman en cuenta cómo funciona un cerebro con déficit de atención.", 'Intentar razonar con él en medio de una crisis es como intentar hablarle a una pared; su cerebro racional está apagado en ese momento.'].map((text, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-49">
                  <div className="flex items-start space-x-4 bg-white p-6 rounded-2xl shadow-sm border border-cream-dark h-full" data-id="element-50">
                    <XCircle className="text-red-400 flex-shrink-0 mt-1" size={24} data-id="element-51" />
                    <p className="text-earth-medium leading-relaxed" data-id="element-52">{text}</p>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-6 bg-terracotta text-white relative overflow-hidden" data-id="element-53">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" data-id="element-54"></div>
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-55">
          <FadeIn data-id="element-56">
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-8" data-id="element-57">
              El Método de la Calma Conectada
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed text-cream font-light max-w-3xl mx-auto" data-id="element-58">
              No es una teoría académica; es una estrategia diseñada para la
              realidad de la madre latina. Se basa en puentes de dopamina y
              regulación sensorial, permitiéndote bajar el volumen de tu voz
              mientras subes el nivel de tu autoridad. Aprendes a comunicarte de
              cerebro a cerebro, eliminando la fricción antes de que se
              convierta en explosión.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 px-6 bg-white" data-id="element-59">
        <div className="max-w-5xl mx-auto" data-id="element-60">
          <FadeIn data-id="element-61">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-16 text-earth-dark" data-id="element-62">
              Lo que encontrarás dentro de la guía
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-63">
              {[{
              title: 'Capítulo 1: El Mapa de la Mente ADHD',
              desc: 'Para que entiendas por qué se bloquea.'
            }, {
              title: 'El Reverso de Culpa',
              desc: "Técnica para eliminar el sentimiento de ser 'mala madre'."
            }, {
              title: 'La Escalera de la Calma',
              desc: 'El paso a paso exacto para dar instrucciones que sí se cumplan.'
            }, {
              title: 'Protocolo de Crisis 3R',
              desc: 'Qué hacer (y qué callar) cuando ocurre un estallido en público o en casa.'
            }, {
              title: 'Guía de Alianza Escolar',
              desc: "Cómo hablar con los maestros para que dejen de ver a tu hijo como 'el problema'."
            }, {
              title: 'Plan de Reset de 7 Días',
              desc: 'Actividades de 10 minutos para reconstruir el amor que el caos ha desgastado.'
            }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-64">
                  <div className="bg-cream-dark/30 p-8 rounded-3xl h-full border border-terracotta/10 hover:border-terracotta/30 transition-colors" data-id="element-65">
                    <BookOpen className="text-terracotta mb-4" size={32} data-id="element-66" />
                    <h3 className="font-serif font-bold text-xl mb-3 text-earth-dark" data-id="element-67">
                      {item.title}
                    </h3>
                    <p className="text-earth-medium" data-id="element-68">{item.desc}</p>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 px-6 bg-cream-dark" data-id="element-69">
        <div className="max-w-5xl mx-auto" data-id="element-70">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-71">
            <FadeIn data-id="element-72">
              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-soft h-full border-t-4 border-green-500" data-id="element-73">
                <h3 className="font-serif text-2xl font-bold mb-8 text-earth-dark flex items-center" data-id="element-74">
                  <CheckCircle2 className="text-green-500 mr-3" size={28} data-id="element-75" />
                  Esto es para ti si...
                </h3>
                <ul className="space-y-6" data-id="element-76">
                  {['Madres latinas con hijos de 5 a 12 años con diagnóstico de ADHD (o sospecha).', 'Mamás que sienten que los gritos se han vuelto su único idioma de comunicación.', 'Mujeres que sufren por las llamadas constantes de la escuela.', 'Madres que aman profundamente a sus hijos pero están al borde del colapso emocional.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-77">
                      <CheckCircle2 className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-78" />
                      <span className="text-earth-medium" data-id="element-79">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-80">
              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-soft h-full border-t-4 border-red-400" data-id="element-81">
                <h3 className="font-serif text-2xl font-bold mb-8 text-earth-dark flex items-center" data-id="element-82">
                  <XCircle className="text-red-400 mr-3" size={28} data-id="element-83" />
                  No es para ti si...
                </h3>
                <ul className="space-y-6" data-id="element-84">
                  {["Personas que buscan una 'pastilla mágica' para que el niño sea un robot obediente.", 'Padres que no están dispuestos a cambiar su propia forma de reaccionar ante el caos.', 'Quienes creen que la violencia física es la única solución para la conducta.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-85">
                      <XCircle className="text-red-400 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-86" />
                      <span className="text-earth-medium" data-id="element-87">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 bg-white" data-id="element-88">
        <div className="max-w-4xl mx-auto" data-id="element-89">
          <FadeIn data-id="element-90">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-16 text-earth-dark" data-id="element-91">
              ¿Cómo funciona?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-92">
              <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-terracotta/20 -z-10" data-id="element-93"></div>
              {[{
              icon: Download,
              title: 'Descarga',
              desc: 'Recibes el acceso inmediato a la guía en PDF en tu correo.'
            }, {
              icon: BookOpenCheck,
              title: 'Lee y Aplica',
              desc: "Comienzas con el Capítulo 1 para entender el 'por qué' de su cerebro."
            }, {
              icon: Users,
              title: 'Conecta',
              desc: 'Implementas el Plan de 7 Días y notas cómo la tensión en tu casa empieza a bajar.'
            }].map((step, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-94">
                  <div className="text-center" data-id="element-95">
                    <div className="w-24 h-24 mx-auto bg-cream rounded-full flex items-center justify-center mb-6 shadow-soft border-4 border-white relative" data-id="element-96">
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-terracotta text-white rounded-full flex items-center justify-center font-bold text-sm" data-id="element-97">
                        {i + 1}
                      </div>
                      <step.icon className="text-terracotta" size={40} data-id="element-98" />
                    </div>
                    <h3 className="font-serif font-bold text-xl mb-3 text-earth-dark" data-id="element-99">
                      {step.title}
                    </h3>
                    <p className="text-earth-medium" data-id="element-100">{step.desc}</p>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-20 px-6 bg-terracotta/5" data-id="element-101">
        <div className="max-w-3xl mx-auto text-center" data-id="element-102">
          <FadeIn data-id="element-103">
            <Heart className="text-terracotta mx-auto mb-6" size={48} data-id="element-104" />
            <p className="text-xl md:text-2xl font-serif italic text-earth-dark leading-relaxed" data-id="element-105">
              "En solo 7 días, verás cómo cambia la narrativa en tu hogar. No
              prometo que el ADHD desaparecerá, pero prometo que pasarás de ser
              'la policía de la casa' a ser la madre conectada que guía con
              firmeza y amor. Verás el primer abrazo real y tranquilo en mucho
              tiempo."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white" data-id="element-106">
        <div className="max-w-3xl mx-auto" data-id="element-107">
          <FadeIn data-id="element-108">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12 text-earth-dark" data-id="element-109">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-2" data-id="element-110">
              <FAQItem question="¿Funciona si mi hijo no está medicado?" answer="Sí, las estrategias son de comportamiento y conexión, independientemente del tratamiento médico." data-id="element-111" />
              <FAQItem question="¿Es muy largo de leer?" answer="No. Está diseñado para mamás sin tiempo; es directo, práctico y sin rellenos." data-id="element-112" />
              <FAQItem question="¿Tengo que comprar materiales extras?" answer="Ninguno. Solo necesitas tu disposición y 10 minutos al día." data-id="element-113" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section id="offer-section" className="py-24 px-6 bg-cream-dark" data-id="element-114">
        <div className="max-w-4xl mx-auto" data-id="element-115">
          <FadeIn data-id="element-116">
            <div className="bg-white rounded-[2.5rem] shadow-xl overflow-hidden border border-terracotta/10" data-id="element-117">
              <div className="bg-terracotta text-white text-center py-4 font-medium tracking-wide" data-id="element-118">
                OFERTA ESPECIAL POR TIEMPO LIMITADO
              </div>
              <div className="p-8 md:p-16 text-center" data-id="element-119">
                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 text-earth-dark" data-id="element-120">
                  Guía Completa: "Mi hijo no me hace caso y ya no sé qué hacer"
                </h2>
                <div className="flex justify-center items-baseline space-x-2 mb-8" data-id="element-121">
                  <span className="text-6xl md:text-7xl font-bold text-terracotta" data-id="element-122">
                    $6.39
                  </span>
                  <span className="text-xl text-earth-medium font-medium" data-id="element-123">
                    USD
                  </span>
                </div>
                <p className="text-lg text-earth-medium max-w-2xl mx-auto mb-10" data-id="element-124">
                  Es menos de lo que cuesta una salida a comer, pero con el
                  poder de transformar los próximos años de tu vida familiar.
                </p>

                <a href="https://jacadigitalhub.com/cart/52247316726053:1" target="_blank" rel="noopener noreferrer" className="w-full md:w-auto bg-terracotta hover:bg-terracotta-dark text-white font-bold text-lg py-5 px-10 rounded-2xl shadow-xl shadow-terracotta/30 transition-all duration-300 hover:-translate-y-1 flex items-center justify-center space-x-3 group mx-auto mb-6 no-underline" data-id="element-125">
                  <span data-id="element-126">QUIERO RECUPERAR LA PAZ EN MI HOGAR AHORA</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={24} data-id="element-127" />
                </a>

                <div className="flex items-center justify-center space-x-2 text-earth-medium text-sm" data-id="element-128">
                  <ShieldCheck className="text-green-500" size={18} data-id="element-129" />
                  <span data-id="element-130">
                    Pago seguro y descarga inmediata. Tu información está
                    protegida.
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-earth-dark text-cream/60 py-12 px-6 text-center text-sm" data-id="element-131">
        <div className="max-w-4xl mx-auto" data-id="element-132">
          <div className="flex items-center justify-center space-x-6 mb-6" data-id="element-133">
            <button onClick={() => setLegalModal({
            open: true,
            type: 'privacy'
          })} className="hover:text-cream transition-colors underline underline-offset-4" data-id="element-134">
              Política de Privacidad
            </button>
            <span className="text-cream/30" data-id="element-135">|</span>
            <button onClick={() => setLegalModal({
            open: true,
            type: 'terms'
          })} className="hover:text-cream transition-colors underline underline-offset-4" data-id="element-136">
              Términos y Condiciones
            </button>
          </div>
          <p className="mb-4" data-id="element-137">
            © {new Date().getFullYear()} Método de la Calma Conectada. Todos
            los derechos reservados.
          </p>
        </div>
      </footer>

      <LegalModal isOpen={legalModal.open} type={legalModal.type} onClose={() => setLegalModal({
      open: false,
      type: legalModal.type
    })} data-id="element-138" />
    </div>;
}